import { shouldReserveTrustArcSpace } from '@/utils/trustArc/trustArcConsentCookies';
import {
  TRUSTARC_BANNER_ID,
  TRUSTARC_CONTAINER_ID,
  TRUSTARC_GEO_VERIFIED_CLASS
} from '@/utils/trustArc/trustArcDom';

export const TRUSTARC_PLACEHOLDER_ID = 'trustarc-placeholder';
export const TRUSTARC_PLACEHOLDER_STYLE_ID = 'trustarc-placeholder-style';

export { TRUSTARC_BANNER_ID, TRUSTARC_CONTAINER_ID, TRUSTARC_GEO_VERIFIED_CLASS };

/** Safety net: never leave reserved space behind if TrustArc never initializes. */
export const TRUSTARC_PLACEHOLDER_TIMEOUT_MS = 10000;

/**
 * The placeholder is injected before the application stylesheet is applied (Tailwind is
 * imported from main.tsx), so it must not rely on utility classes for its position or
 * size, otherwise it renders unstyled at the top of the document until the CSS lands.
 * Everything it needs is inlined here, mirroring the container's
 * `fixed inset-x-0 bottom-0 z-sticky-header` and the `Shimmer` component's look.
 */
const PLACEHOLDER_STYLES = `
#${TRUSTARC_PLACEHOLDER_ID} {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.75rem;
  height: 13.5rem;
  padding: 1.25rem 1rem;
  background-color: #ffffff;
  border-top: 1px solid #ececec;
  pointer-events: none;
}
#${TRUSTARC_PLACEHOLDER_ID} > span {
  display: block;
  height: 1rem;
  border-radius: 0.25rem;
  background-color: #ececec;
  animation: trustarc-placeholder-pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
@media (min-width: 640px) {
  #${TRUSTARC_PLACEHOLDER_ID} { height: 9.5rem; }
}
@media (min-width: 768px) {
  #${TRUSTARC_PLACEHOLDER_ID} { height: 7.5rem; padding: 1.25rem 2rem; }
}
@media (prefers-reduced-motion: reduce) {
  #${TRUSTARC_PLACEHOLDER_ID} > span { animation: none; }
}
@keyframes trustarc-placeholder-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}
`;

const SHIMMER_LINES: { width: string; height?: string }[] = [
  { width: '75%' },
  { width: '60%' },
  { width: '10rem', height: '2.25rem' }
];

const ensureStyles = (): void => {
  if (document.getElementById(TRUSTARC_PLACEHOLDER_STYLE_ID)) return;

  const style = document.createElement('style');
  style.id = TRUSTARC_PLACEHOLDER_STYLE_ID;
  style.textContent = PLACEHOLDER_STYLES;
  document.head.appendChild(style);
};

const createPlaceholder = (): HTMLElement => {
  const placeholder = document.createElement('div');
  placeholder.id = TRUSTARC_PLACEHOLDER_ID;
  placeholder.setAttribute('aria-hidden', 'true');
  placeholder.setAttribute('inert', '');
  placeholder.setAttribute('data-component', 'TrustArcPlaceholder');

  SHIMMER_LINES.forEach(({ width, height }) => {
    const line = document.createElement('span');
    line.style.width = width;
    if (height) line.style.height = height;
    placeholder.appendChild(line);
  });

  return placeholder;
};

const hasRealBanner = (container: HTMLElement): boolean =>
  Array.from(container.children).some(
    child => child.id !== TRUSTARC_PLACEHOLDER_ID && child.childElementCount > 0
  );

/**
 * The placeholder must outlive the banner's DOM insertion: TrustArc injects its markup well
 * before the geo gate reveals it. Removing the reservation any earlier re-opens the exact
 * layout shift this module prevents, so require the banner to be both present and visible.
 */
const isRealBannerVisible = (container: HTMLElement): boolean => {
  if (!hasRealBanner(container)) return false;
  if (!container.classList.contains(TRUSTARC_GEO_VERIFIED_CLASS)) return false;

  const banner = document.getElementById(TRUSTARC_BANNER_ID);

  // jsdom has no layout engine, so offsetParent/rects are unreliable there; the geo gate
  // above is the authoritative signal and this is a defensive check for real browsers.
  if (banner && typeof window.getComputedStyle === 'function') {
    const { display, visibility } = window.getComputedStyle(banner);
    if (display === 'none' || visibility === 'hidden') return false;
  }

  return true;
};

/**
 * Reserves the space the TrustArc consent banner will occupy for first-time visitors and
 * fills it with a shimmer until the real banner renders. This keeps the late, bottom
 * anchored banner injection from shifting the page (CLS) on a first visit.
 */
export const initTrustArcPlaceholder = (): (() => void) | undefined => {
  const container = document.getElementById(TRUSTARC_CONTAINER_ID);

  if (!container || document.getElementById(TRUSTARC_PLACEHOLDER_ID)) return undefined;
  if (!shouldReserveTrustArcSpace() || isRealBannerVisible(container)) return undefined;

  ensureStyles();

  const placeholder = createPlaceholder();
  container.insertBefore(placeholder, container.firstChild);

  function removePlaceholder(): void {
    observer.disconnect();
    window.clearTimeout(timeoutId);
    placeholder.remove();
    document.getElementById(TRUSTARC_PLACEHOLDER_STYLE_ID)?.remove();
  }

  const observer = new MutationObserver(() => {
    if (isRealBannerVisible(container)) removePlaceholder();
  });
  observer.observe(container, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'style']
  });

  const timeoutId = window.setTimeout(removePlaceholder, TRUSTARC_PLACEHOLDER_TIMEOUT_MS);

  return removePlaceholder;
};
